// @flow

import _ from 'lodash';

import ApiObject from './ApiObject';

import OpeningHours from './OpeningHours';
import RestaurantColors from './RestaurantColors';
import Address from './Address';

/**
 * Simple entity to update parts of a restaurant.
 */
export default class RestaurantMultipart extends ApiObject {
  // -- otherParameters

  name: ?string = null;
  webSitePub: ?string = null;
  externalId: ?string = null;
  desc: ?string = null;
  tag: ?string = null;
  facebookPub: ?string = null;
  twitterPub: ?string = null;
  phonePub: ?string = null;
  emailPub: ?string = null;
  openingHours: ?OpeningHours = null;
  colors: ?RestaurantColors = null;
  address: ?Address = null;
  timeZone = null;
  password = null;
  passwordVerification = null;

  pos: ?string = null;
  posUrl: ?string = null;
  posId: ?string = null;
  posMenuId: ?string = null;

  posCustomerId: ?string = null;
  posCommentId: ?string = null;
  posCredentials: ?string = null;
  posEncryptionKey: ?string = null;

  geoPt = null;

  serviceType = null;

  features = null;

  valided = null;

  allowTips: boolean = null;

  // -- files

  /**
   * Must be a png file.
   * @param coverPicture
   */
  coverPicture: ?string = null;

  /**
   * Must be a png file.
   * @param coverPicture
   */
  coverPictureFullSize: ?string = null;

  /**
   * Must be a png file.
   * @param coverPicture
   */
  logo: ?string = null;

  menuPdf = null;

  menuPicture = null;

  legal = null;

  legalBankIdentityDocument = null;

  legalShareholderDeclarationFile = null;

  legalStatuteFile = null;

  legalRegistrationProofFile = null;

  legalRepresentativeIdentityDocument = null;

  proofOfIncome = null;

  // -- bank
  acquirer = null;
  psp = null;
  pspSettings = null;

  // -- get data for request

  getOtherParameters(): Object {
    const otherParameters = {};

    if (!_.isNil(this.name)) {
      otherParameters.name = this.name;
    }

    if (!_.isNil(this.externalId)) {
      otherParameters.externalId = this.externalId;
    }

    if (!_.isNil(this.webSitePub)) {
      otherParameters.webSitePub = this.webSitePub;
    }

    if (!_.isNil(this.desc)) {
      otherParameters.desc = this.desc;
    }

    if (!_.isNil(this.tag)) {
      otherParameters.tag = this.tag;
    }

    if (!_.isNil(this.facebookPub)) {
      otherParameters.facebookPub = this.facebookPub;
    }

    if (!_.isNil(this.twitterPub)) {
      otherParameters.twitterPub = this.twitterPub;
    }

    if (!_.isNil(this.phonePub)) {
      otherParameters.phonePub = this.phonePub;
    }

    if (!_.isNil(this.emailPub)) {
      otherParameters.emailPub = this.emailPub;
    }

    if (!_.isNil(this.address)) {
      otherParameters.address = this.address;
    }

    if (!_.isNil(this.timeZone)) {
      otherParameters.timeZone = this.timeZone;
    }

    if (!_.isNil(this.locale)) {
      otherParameters.locale = this.locale;
    }

    if (!_.isEmpty(this.password)) {
      otherParameters.password = this.password;
    }

    if (!_.isEmpty(this.password) && !_.isEmpty(this.passwordVerification)) {
      otherParameters.passwordVerification = this.passwordVerification;
    }

    if (!_.isNil(this.openingHours)) {
      otherParameters.openingHours = this.openingHours;
    }

    if (!_.isNil(this.colors)) {
      otherParameters.colors = this.colors;
    }

    if (!_.isNil(this.pos)) {
      otherParameters.pos = this.pos;
    }

    if (!_.isNil(this.posId)) {
      otherParameters.posId = this.posId;
    }

    if (!_.isNil(this.posUrl)) {
      otherParameters.posUrl = this.posUrl;
    }

    if (!_.isNil(this.posMenuId)) {
      otherParameters.posMenuId = this.posMenuId;
    }

    if (!_.isNil(this.posCustomerId)) {
      otherParameters.posCustomerId = this.posCustomerId;
    }

    if (!_.isNil(this.posCommentId)) {
      otherParameters.posCommentId = this.posCommentId;
    }

    if (!_.isNil(this.posPriceLevelId)) {
      otherParameters.posPriceLevelId = this.posPriceLevelId;
    }

    if (!_.isNil(this.posUseQsrOrderNumber)) {
      otherParameters.posUseQsrOrderNumber = this.posUseQsrOrderNumber;
    }

    if (!_.isNil(this.posSendArticleLineNum)) {
      otherParameters.posSendArticleLineNum = this.posSendArticleLineNum;
    }

    if (!_.isNil(this.posTipArticleId)) {
      otherParameters.posTipArticleId = this.posTipArticleId;
    }

    if (!_.isNil(this.posCredentials)) {
      otherParameters.posCredentials = this.posCredentials;
    }

    if (!_.isNil(this.posEncryptionKey)) {
      otherParameters.posEncryptionKey = this.posEncryptionKey;
    }

    if (!_.isNil(this.legal)) {
      otherParameters.legal = this.legal;
    }

    if (!_.isNil(this.allowTips)) {
      otherParameters.allowTips = this.allowTips;
    }

    if (!_.isNil(this.serviceType)) {
      otherParameters.serviceType = this.serviceType;
    }

    if (!_.isNil(this.features)) {
      otherParameters.features = this.features;
    }

    if (!_.isNil(this.status)) {
      otherParameters.status = this.status;
    }

    if (!_.isNil(this.geoPt)) {
      otherParameters.geoPt = this.geoPt;
    }

    if (!_.isNil(this.psp)) {
      otherParameters.psp = this.psp;
    }

    if (!_.isNil(this.acquirer)) {
      otherParameters.acquirer = this.acquirer;
    }

    if (!_.isNil(this.pspSettings)) {
      otherParameters.pspSettings = this.pspSettings;
    }

    return otherParameters;
  }

  getData(): Object {
    const data = {};

    const otherParameters = this.getOtherParameters();

    if (!_.isEmpty(otherParameters)) {
      data.otherParameters = JSON.stringify(otherParameters);
    }

    return data;
  }

  getFiles(): Object {
    const filesData = {};

    if (!_.isNull(this.coverPicture)) {
      /**
       * with content-type “image/png” and containing the cropped image for the mobile app to display
       * @type {null}
       */
      filesData.picture = this.coverPicture;
    }

    if (!_.isNull(this.coverPictureFullSize)) {
      /**
       *  with content-type “image/png” or “image/jpg” and containing the original full size
       *  (non-cropped) version of the picture image
       * @type {null}
       */
      filesData.pictureFullSize = this.coverPictureFullSize;
    }

    if (!_.isNull(this.logo)) {
      /**
       *  with content-type “image/png” and containing the logo of the restaurant
       * @type {null}
       */
      filesData.logo = this.logo;
    }

    if (!_.isNil(this.menuPdf)) {
      filesData.menuPdf = this.menuPdf;
    }

    if (!_.isNil(this.menuPicture)) {
      filesData.menuPicture = this.menuPicture;
    }

    // -- legal
    if (!_.isNull(this.legalStatuteFile)) {
      /**
       * with content-type “application/pdf” and containing the statute document of the legal business
       * @type {null}
       */
      filesData['legal.statute'] = this.legalStatuteFile;
    }

    if (!_.isNull(this.legalRegistrationProofFile)) {
      /**
       * with content-type “application/pdf” and containing the proof of registration for
       * the legal business (k-bis)
       * @type {null}
       */
      filesData['legal.registrationProof'] = this.legalRegistrationProofFile;
    }

    if (!_.isNull(this.legalShareholderDeclarationFile)) {
      /**
       *
       * @type {null}
       */
      filesData[
        'legal.shareholderDeclaration'
      ] = this.legalShareholderDeclarationFile;
    }

    // -- legal representative

    if (!_.isNull(this.legalRepresentativeIdentityDocument)) {
      /**
       * with content-type “application/pdf” and containing a copy of the identity card or passport
       * of the legal representative of the legal business
       * @type {null}
       */
      filesData[
        'legal.representative.identityDocument'
      ] = this.legalRepresentativeIdentityDocument;
    }

    // -- legal bank

    if (!_.isNull(this.legalBankIdentityDocument)) {
      /**
       * with content-type “application/pdf” and containing a copy of the bank identity of the legal
       * business (the document must match the IBAN, BIC and Business Legal Name informations).
       * There are no verifications though.
       */
      filesData['legal.bank.identityDocument'] = this.legalBankIdentityDocument;
    }

    if (!_.isNull(this.proofOfIncome)) {
      filesData['legal.PROOF_OF_INCOME'] = this.proofOfIncome;
    }

    return filesData;
  }
}



// WEBPACK FOOTER //
// ./sdk/scripts/entities/RestaurantMultipart.js